// Hợp đồng API của module `ai` (Zod schema + kiểu response).
import { z } from 'zod';
/**
 * Màn hình có nút «Phân tích AI». Giá trị là DỮ LIỆU đang lưu (cột `ai_usage.screen` và nhật
 * ký của hệ thống cũ đều ghi đúng các chuỗi này) nên giữ nguyên, kể cả `tuan`.
 */
export const AI_SCREENS = ['month', 'tuan', 'market', 'prodkpi', 'report', 'revenue'];
export const AI_SCREEN_LABELS = {
    month: 'Chỉ số tháng',
    tuan: 'Chỉ số tuần',
    market: 'Thị trường — đối thủ trực tiếp & gián tiếp',
    /** Ba màn «Tổng quan nhận xét» (02/10/2026): ai chưa báo cáo / chưa nhập, và nhận xét nội dung, số liệu. */
    prodkpi: 'Tiến độ hằng ngày của Producer',
    report: 'Báo cáo công việc hằng ngày',
    revenue: 'Doanh thu kênh',
};
/**
 * Mã đơn vị đặt trên ĐƯỜNG DẪN để chỉ «khoá chung của công ty» (trong CSDL là chuỗi rỗng, mà
 * đoạn đường dẫn thì không rỗng được). Dấu `~` không nằm trong bộ ký tự của mã phòng ban
 * (`[a-z0-9._-]`) nên không bao giờ trùng một phòng thật.
 */
export const SHARED_AI_KEY_ID = '~shared';
/**
 * Cố ý LỎNG: từng trường được kiểm trong service để giữ nguyên văn câu báo lỗi của hệ thống
 * cũ («Không rõ đang phân tích màn hình nào»…) thay vì một câu chung chung.
 */
export const aiAnalysisRequestSchema = z.object({
    screen: z.unknown().optional(),
    /** ĐÚNG bảng số đang hiện trên màn hình người bấm. */
    table: z.unknown().optional(),
    /** Câu hỏi thêm (tuỳ chọn) — cắt còn 2000 ký tự. */
    question: z.unknown().optional(),
});
export const aiAnalysisParamsSchema = z.object({ id: z.string() });
// ---------------------------------------------------------------- khoá API & mức dùng
export const aiMonthQuerySchema = z.object({
    /** 'YYYY-MM'. Thiếu hoặc sai dạng → tháng hiện tại (UTC), như hệ thống cũ. */
    month: z.string().optional(),
});
export const aiKeyParamsSchema = z.object({ departmentId: z.string() });
/** Lỏng có chủ đích: câu báo lỗi về khoá do `findApiKeyProblem` sinh ra, nguyên văn bản cũ. */
export const aiKeySaveRequestSchema = z.object({ apiKey: z.unknown().optional() });
// ---------------------------------------------------------------- viết mô tả / từ khoá kênh bằng AI
/**
 * «VIẾT BẰNG AI» ở Đăng video › Tối ưu kênh (06/10/2026, chủ dự án: «Tôi muốn dùng API để tạo ra mô tả
 * kênh, từ khóa kênh»). Trình duyệt gửi những gì nó đang có — tên kênh, mô tả và từ khoá đang gõ, tên
 * video gần đây, yêu cầu thêm — máy chủ không gọi YouTube. Cũng hai chặng như Phân tích AI (202 + hỏi lại).
 */
export const AI_CHANNEL_TITLES_MAX = 40;
export const AI_CHANNEL_BRIEF_MAX = 500;
export const aiChannelSuggestRequestSchema = z.object({
    title: z.string().trim().max(200),
    /** Mô tả đang gõ — chỉ làm ngữ cảnh, nên nhận rộng hơn trần 1000 của YouTube. */
    description: z.string().max(4000),
    keywords: z.array(z.string().trim().max(100)).max(200),
    country: z.string().trim().max(20),
    defaultLanguage: z.string().trim().max(20),
    /** Tên video gần đây, mới nhất trước. */
    titles: z.array(z.string().trim().max(200)).max(AI_CHANNEL_TITLES_MAX),
    brief: z.string().trim().max(AI_CHANNEL_BRIEF_MAX),
});
/**
 * VIẾT TIÊU ĐỀ · MÔ TẢ · TAG VIDEO BẰNG AI (06/10/2026, chủ dự án: «dùng AI để phân tích làm tiêu đề và mô
 * tả»). Trình duyệt gửi những gì đang có (tên file / tiêu đề, mô tả, tag, mẫu SEO của kênh, tên video gần đây);
 * máy chủ thêm tiêu đề đối thủ nhiều lượt xem từ kho Nghiên cứu đối thủ rồi mới hỏi Claude. Dùng cho video tải
 * lên, video đã trên kênh và buổi phát trực tiếp.
 */
export const AI_VIDEO_KINDS = ['video', 'live'];
export const AI_VIDEO_TITLES_MAX = 40;
export const AI_VIDEO_COMPETITOR_TITLES_MAX = 20;
export const AI_VIDEO_BRIEF_MAX = 500;
export const aiVideoSeoRequestSchema = z.object({
    kind: z.enum(AI_VIDEO_KINDS).default('video'),
    channelTitle: z.string().trim().max(200),
    /** Tiêu đề đang có hay tên file — đầu mối chính về nội dung. */
    title: z.string().trim().max(300),
    description: z.string().max(6000),
    tags: z.array(z.string().trim().max(100)).max(200),
    /** Mẫu SEO của kênh (ô trống = không có) — AI viết hợp khung mẫu. */
    template: z
        .object({
        title: z.string().max(300).default(''),
        description: z.string().max(6000).default(''),
        tags: z.string().max(2000).default(''),
    })
        .default({ title: '', description: '', tags: '' }),
    /** Tên video gần đây của kênh, mới nhất trước — giữ giọng điệu của kênh. */
    titles: z.array(z.string().trim().max(200)).max(AI_VIDEO_TITLES_MAX),
    brief: z.string().trim().max(AI_VIDEO_BRIEF_MAX),
});
/**
 * TẠO DANH SÁCH PHÁT BẰNG AI (07/10/2026, chủ dự án: «dùng AI tạo thêm danh sách phát»): Claude đề xuất tên + mô
 * tả một danh sách phát mới hợp với video đang sửa và cách đặt tên của kênh (không trùng danh sách đang có);
 * tạo thật là lần bấm riêng (`POST …/playlists`).
 */
export const AI_PLAYLIST_BRIEF_MAX = 500;
export const AI_PLAYLIST_TITLES_MAX = 100;
export const aiPlaylistSuggestRequestSchema = z.object({
    channelTitle: z.string().trim().max(200),
    video: z.object({
        title: z.string().trim().max(300),
        description: z.string().max(6000),
        tags: z.array(z.string().trim().max(100)).max(200),
    }),
    /** Tên danh sách phát đang có của kênh — để không trùng và hợp cách đặt tên. */
    playlists: z.array(z.string().trim().max(200)).max(AI_PLAYLIST_TITLES_MAX),
    titles: z.array(z.string().trim().max(200)).max(AI_VIDEO_TITLES_MAX),
    brief: z.string().trim().max(AI_PLAYLIST_BRIEF_MAX),
});
//# sourceMappingURL=ai.js.map